import { type FC, useEffect, useMemo } from 'react';
import { Link } from 'wouter';

import { categoryGamesRequest } from 'api/casino';
import classNames from 'classnames';
import GameTile from 'components/GameTile';
import Loader from 'components/Loader';
import { CasinoGroup } from 'utils/casino';
import pushToDataLayer, { btag } from 'utils/dataLayer';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useSliceRequest from 'utils/useSliceRequest';

import styles from './CasinoGames.module.scss';

const GAMES_COUNT_BY_LAYOUT = {
  [LayoutType.wide]: 9,
  [LayoutType.tablet]: 11,
  [LayoutType.mobile]: 8,
};

const Games: FC = () => {
  const layout = useLayout();

  const categoryGamesProps = useMemo(
    () => ({
      query: {
        groupName: CasinoGroup.CASINO,
        gamesCount: GAMES_COUNT_BY_LAYOUT[layout],
      },
      slice: {
        itemsPerSlice: 10,
      },
    }),
    [layout],
  );

  const { data, loading, loadMore } = useSliceRequest(
    categoryGamesRequest,
    categoryGamesProps,
  );

  const games = useMemo(() => {
    if (loading !== LoadingState.success) return [];

    const category = data.reduce(
      (minItem, currentItem) =>
        currentItem.position < minItem.position ? currentItem : minItem,
      data[0],
    );

    return category?.games ?? [];
  }, [data, loading]);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  //Initial includes, cause we have AllGames static card
  const isGamesLoading =
    loading === LoadingState.initial || loading === LoadingState.loading;

  if (isGamesLoading) {
    return (
      <div className={classNames(styles.loader, styles[layout])}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classNames(styles.games, styles[layout])}>
      <Link
        to="/casino"
        onClick={() => pushToDataLayer('main_all_games_tumblr_click', btag)}
        className={classNames(styles['all-games'], styles[layout])}
      >
        <div className={classNames(styles.label, styles[layout])}>
          <Translation message="all_games" />
        </div>
      </Link>
      {games.map((game) => (
        <GameTile
          key={game.id}
          className={classNames(styles.game, styles[layout])}
          iconUrl={game.iconUrl}
          id={game.id}
          name={game.name}
          isDemoAvailable={game.isDemoAvailable}
          providerName={game.providerName}
          favoritesCount={game.favoritesCount}
        />
      ))}
    </div>
  );
};

export default Games;
